import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import SEOHead from "@/components/site/SEOHead";
import Reveal from "@/components/site/Reveal";
import CTA from "@/components/site/CTA";
import SmartMedia from "@/components/site/SmartMedia";
import Lightbox from "@/components/site/Lightbox";
import { CustomSection } from "@/components/site/PropertySections";
import { localBusiness } from "@/lib/schema";
import { useSettings } from "@/lib/settings";
import { useEnquiry } from "@/components/site/PublicLayout";
import { usePageContent } from "@/lib/content";

const Icon = ({ name, ...p }) => {
  const C = Icons[name] || Icons.Sparkles;
  return <C {...p} />;
};

export default function Home() {
  const enquiry = useEnquiry();
  const { settings } = useSettings();
  const { content: c } = usePageContent("home");
  const [properties, setProperties] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [posts, setPosts] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [lbIndex, setLbIndex] = useState(null);

  useEffect(() => {
    api.get("/public/properties").then((r) => setProperties(r.data || []));
    api.get("/public/testimonials").then((r) => setTestimonials(r.data || []));
    api.get("/public/blog").then((r) => setPosts((r.data || []).slice(0, 3)));
    api.get("/public/gallery").then((r) => setGallery((r.data || []).slice(0, 6)));
  }, []);

  if (!c) return <div className="pt-32 pb-32 container-x"><div className="animate-pulse h-96 bg-bone" /></div>;

  const openEnquiry = () => enquiry.open("");
  const hero = c.hero || {};
  const trust = c.trust_strip || {};
  const story = c.brand_story || {};
  const propSec = c.properties_section || {};
  const cat = c.categories || {};
  const tSec = c.testimonial_section || {};
  const why = c.why_section || {};
  const pblk = c.partner_block || {};
  const galSec = c.gallery_section || {};
  const blogSec = c.blog_section || {};
  const closing = c.closing_cta || {};

  return (
    <>
      <SEOHead schema={localBusiness(settings, testimonials)} />

      {/* HERO */}
      {hero.enabled !== false && (
        <section className="relative min-h-[100svh] flex items-end overflow-hidden">
          <SmartMedia media={hero.image_url} alt="Happy Retreats" className="absolute inset-0 w-full h-full object-cover" eager />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-ink/15 to-ink/75" />
          <div className="relative container-x pb-20 md:pb-32 pt-44 md:pt-52 text-ivory">
            {hero.overline && <div className="overline text-ivory/85 animate-fade-up" style={{ animationDelay: "0.2s" }}>{hero.overline}</div>}
            <h1 className="display-1 mt-8 max-w-5xl animate-fade-up" style={{ animationDelay: "0.4s" }}>
              {hero.headline_pre} {hero.headline_accent ? <span className="accent-block"><span className="italic text-clay">{hero.headline_accent}</span>{hero.headline_post}</span> : hero.headline_post}
            </h1>
            {hero.subheadline && <p className="mt-8 max-w-2xl text-base md:text-lg text-ivory/85 font-light leading-relaxed animate-fade-up" style={{ animationDelay: "0.6s" }}>{hero.subheadline}</p>}
            {hero.show_cta !== false && (
              <div className="mt-12 flex flex-wrap gap-4 animate-fade-up" style={{ animationDelay: "0.8s" }}>
                <CTA config={hero.primary_cta} onEnquire={openEnquiry} variant="onDarkPrimary" testId="hero-enquire" />
                <CTA config={hero.secondary_cta} onEnquire={openEnquiry} variant="onDarkGhost" testId="hero-partner" />
              </div>
            )}
          </div>
        </section>
      )}

      {/* TRUST STRIP */}
      {trust.enabled !== false && trust.metrics?.length > 0 && (
        <section id="trust" className="bg-ink text-ivory border-y border-ivory/10" data-testid="trust-strip">
          <div className="container-x grid grid-cols-2 md:grid-cols-4 divide-x divide-ivory/10">
            {trust.metrics.map((t, i) => (
              <Reveal key={i} delay={i * 80} className="py-10 md:py-12 px-6 first:pl-0 last:pr-0">
                <div className="font-display text-3xl md:text-4xl lg:text-5xl text-ivory">{t.value}</div>
                <div className="mt-3 text-[10px] md:text-xs text-ivory/60 uppercase tracking-[0.18em] leading-snug">{t.label}</div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* BRAND STORY */}
      {story.enabled !== false && (
        <section id="story" className="container-x py-8 md:py-12" data-testid="brand-story">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              {story.overline && <Reveal><div className="overline">{story.overline}</div></Reveal>}
              <Reveal delay={150}>
                <h2 className="display-2 mt-6">
                  {story.headline_pre}
                  {story.headline_accent && <em className="font-display italic text-clay accent-block">{story.headline_accent}</em>}
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 para-stack body-lead">
              {(story.paragraphs || []).map((p, i) => (
                <Reveal key={i} delay={100 + i * 100}>
                  <p className={i === (story.paragraphs.length - 1) ? "text-ink font-medium" : ""}>{p}</p>
                </Reveal>
              ))}
              {story.show_cta !== false && story.link_label && story.link_url && (
                <Reveal delay={400}>
                  <Link to={story.link_url} className="link-arrow mt-4" data-testid="story-partner-link">
                    {story.link_label} <ArrowRight className="w-4 h-4" />
                  </Link>
                </Reveal>
              )}
            </div>
          </div>
        </section>
      )}

      <div className="hairline" />

      {/* FEATURED PROPERTIES */}
      {propSec.enabled !== false && (
        <section id="properties" className="py-8 md:py-12 bg-bone" data-testid="properties-section">
          <div className="container-x">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
              <div>
                {propSec.overline && <Reveal><div className="overline">{propSec.overline}</div></Reveal>}
                {propSec.headline && <Reveal delay={100}><h2 className="display-2 mt-6 max-w-2xl">{propSec.headline}</h2></Reveal>}
              </div>
              {propSec.caption && <Reveal delay={200}><p className="body-lead max-w-md">{propSec.caption}</p></Reveal>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 lg:gap-10">
              {properties.map((p, idx) => (
                <Reveal key={p.id} delay={idx * 100} className="group aspect-[4/5]">
                  <Link to={`/${p.slug}`} className="relative block w-full h-full zoom-on-hover overflow-hidden" data-testid={`property-card-${p.slug}`}>
                    <SmartMedia media={p.hero?.image_url} alt={p.name} className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-ink/75" />
                    <div className="absolute inset-x-0 bottom-0 p-8 md:p-10 text-ivory">
                      <div className="number-marker text-ivory/60">Getaway · {String(idx + 1).padStart(2, "0")}</div>
                      <div className="font-display text-4xl md:text-5xl mt-3 leading-none">{p.name}</div>
                      <div className="mt-2 text-sm text-ivory/85 italic font-light">{p.tagline}</div>
                      {propSec.show_cta !== false && <div className="mt-4 link-arrow text-ivory">View getaway <ArrowRight className="w-4 h-4" /></div>}
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CATEGORIES */}
      {cat.enabled !== false && cat.items?.length > 0 && (
        <section id="categories" className="py-8 md:py-12 container-x" data-testid="categories-section">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              {cat.overline && <Reveal><div className="overline">{cat.overline}</div></Reveal>}
              {cat.headline && <Reveal delay={100}><h2 className="display-2 mt-6 max-w-3xl">{cat.headline}</h2></Reveal>}
            </div>
            {cat.caption && <Reveal delay={200}><p className="body-lead max-w-md">{cat.caption}</p></Reveal>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-ink/10">
            {cat.items.map((it, i) => (
              <Reveal key={i} delay={i * 100}>
                <Link to={it.url || "#"} className="block bg-ivory group" data-testid={`category-${i}`}>
                  <div className="aspect-[4/5] overflow-hidden zoom-on-hover relative"><SmartMedia media={it.image} alt={it.label} className="w-full h-full object-cover" /></div>
                  <div className="p-8">
                    <div className="flex items-center gap-3">
                      <Icon name={it.icon} className="w-5 h-5 text-clay" strokeWidth={1.4} />
                      <div className="font-mono text-[10px] tracking-[0.3em] text-clay">{it.kw}</div>
                    </div>
                    <div className="font-display text-3xl mt-4">{it.label}</div>
                    <div className="mt-3 text-sm text-graphite leading-relaxed font-light">{it.caption}</div>
                    {cat.show_cta !== false && <div className="link-arrow mt-5">Explore <ArrowRight className="w-3.5 h-3.5" /></div>}
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* TESTIMONIAL */}
      {tSec.enabled !== false && testimonials[0] && (
        <section id="testimonials" className="bg-ink text-ivory py-8 md:py-12 relative overflow-hidden" data-testid="testimonial-section">
          <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
          <div className="container-x relative">
            {tSec.overline && <Reveal><div className="overline text-ivory/60">{tSec.overline}</div></Reveal>}
            <Reveal delay={150}>
              <blockquote className="display-2 mt-10 max-w-4xl text-ivory">
                <span className="font-display italic text-clay">&ldquo;</span>{testimonials[0].text}<span className="font-display italic text-clay">&rdquo;</span>
              </blockquote>
            </Reveal>
            <Reveal delay={300}>
              <div className="mt-10 flex items-center gap-4 text-sm text-ivory/70">
                <span className="font-medium text-ivory">{testimonials[0].name}</span>
                <span className="h-px w-10 bg-ivory/30" />
                <span>{testimonials[0].source}</span>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* WHY */}
      {why.enabled !== false && why.items?.length > 0 && (
        <section id="why" className="py-8 md:py-12 container-x" data-testid="why-section">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              {why.overline && <Reveal><div className="overline">{why.overline}</div></Reveal>}
              <Reveal delay={100}><h2 className="display-2 mt-6">{why.headline_pre} {why.headline_accent && <em className="italic text-clay">{why.headline_accent}</em>}{why.headline_post}</h2></Reveal>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-12">
              {why.items.map((w, i) => (
                <Reveal key={i} delay={i * 80}>
                  <div>
                    <div className="number-marker">{w.kw}</div>
                    <div className="font-display text-2xl mt-3">{w.title}</div>
                    <div className="mt-3 text-sm text-graphite leading-relaxed font-light">{w.body}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PARTNER BLOCK */}
      {pblk.enabled !== false && (
        <section id="partner" className="relative py-8 md:py-12 overflow-hidden" data-testid="partner-section">
          <SmartMedia media={pblk.image_url} alt="Operate with Happy Retreats" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-ink/85" />
          <div className="absolute inset-0 grain opacity-20 pointer-events-none" />
          <div className="container-x relative text-ivory">
            {pblk.overline && <Reveal><div className="overline text-ivory/60">{pblk.overline}</div></Reveal>}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-6">
              <Reveal delay={100} className="lg:col-span-7">
                <h2 className="display-2 text-ivory">{pblk.headline_pre}{pblk.headline_accent && <em className="italic text-clay accent-block">{pblk.headline_accent}</em>}</h2>
                {pblk.body && <p className="mt-8 max-w-xl text-ivory/80 font-light leading-relaxed text-base md:text-lg">{pblk.body}</p>}
                {pblk.show_cta !== false && (
                  <div className="mt-10 flex flex-wrap gap-4">
                    <CTA config={pblk.primary_cta} onEnquire={openEnquiry} variant="onDarkPrimary" testId="partner-explore" />
                    <CTA config={pblk.secondary_cta} onEnquire={openEnquiry} variant="onDarkGhost" testId="partner-submit" />
                  </div>
                )}
              </Reveal>
              <Reveal delay={250} className="lg:col-span-5">
                <div className="space-y-px">
                  {(pblk.pillars || []).map((p, i) => (
                    <div key={i} className="bg-white/5 backdrop-blur-md p-8 border-l-2 border-clay">
                      <div className="flex items-center gap-3 text-clay"><Icon name={p.icon} className="w-5 h-5" strokeWidth={1.4} /><div className="overline text-clay">0{i + 1}</div></div>
                      <div className="font-display text-2xl mt-3 text-ivory">{p.title}</div>
                      <div className="mt-2 text-sm text-ivory/70 leading-relaxed font-light">{p.body}</div>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* GALLERY */}
      {galSec.enabled !== false && gallery.length > 0 && (
        <section id="gallery" className="bg-bone py-8 md:py-12" data-testid="gallery-section">
          <div className="container-x">
            <div className="flex items-end justify-between mb-12">
              <div>
                {galSec.overline && <Reveal><div className="overline">{galSec.overline}</div></Reveal>}
                {galSec.headline && <Reveal delay={100}><h2 className="display-2 mt-6">{galSec.headline}</h2></Reveal>}
              </div>
              {galSec.show_cta !== false && <Reveal delay={200}><Link to="/blog" className="link-arrow hidden md:inline-flex">All stories <ArrowRight className="w-4 h-4" /></Link></Reveal>}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
              {gallery.map((g, i) => (
                <Reveal key={g.id} delay={i * 60} className={i % 5 === 0 ? "md:col-span-2 md:row-span-2 aspect-square" : "aspect-[4/5]"}>
                  <button type="button" onClick={() => setLbIndex(i)} className="w-full h-full zoom-on-hover relative cursor-zoom-in block text-left" aria-label={`View photo ${i + 1}`} data-testid={`home-gallery-item-${i}`}>
                    <SmartMedia media={g.url} alt={g.alt || "Gallery"} className="w-full h-full object-cover" />
                  </button>
                </Reveal>
              ))}
            </div>
            <Lightbox items={gallery.map((g) => g.url)} index={lbIndex} setIndex={setLbIndex} altPrefix="Gallery" />
          </div>
        </section>
      )}

      {/* BLOG */}
      {blogSec.enabled !== false && posts.length > 0 && (
        <section id="stories" className="container-x py-8 md:py-12" data-testid="blog-section">
          <div className="flex items-end justify-between mb-12">
            <div>
              {blogSec.overline && <Reveal><div className="overline">{blogSec.overline}</div></Reveal>}
              {blogSec.headline && <Reveal delay={100}><h2 className="display-2 mt-6">{blogSec.headline}</h2></Reveal>}
            </div>
            {blogSec.show_cta !== false && <Reveal delay={200}><Link to="/blog" className="link-arrow">All stories <ArrowRight className="w-4 h-4" /></Link></Reveal>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {posts.map((p, i) => (
              <Reveal key={p.id} delay={i * 100}>
                <Link to={`/blog/${p.slug}`} className="block group" data-testid={`blog-card-${i}`}>
                  <div className="aspect-[4/3] zoom-on-hover overflow-hidden relative"><SmartMedia media={p.cover_image} alt={p.title} className="w-full h-full object-cover" /></div>
                  <div className="overline mt-6">{p.category}</div>
                  <div className="font-display text-2xl mt-3 leading-tight group-hover:text-clay transition-colors">{p.title}</div>
                  <div className="mt-3 text-sm text-graphite font-light leading-relaxed">{p.excerpt}</div>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* CUSTOM SECTIONS (drag-and-drop builder) */}
      {(c.layout || []).filter((s) => s.enabled !== false).map((sec) => (
        <CustomSection key={sec.id} sec={sec} enquiry={enquiry} slug="home" />
      ))}

      {/* CLOSING CTA */}
      {closing.enabled !== false && (
        <section className="container-x pb-24 md:pb-32" data-testid="cta-section">
          <div className="bg-ink text-ivory p-10 md:p-20 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            <Reveal>
              <h2 className="display-2 text-ivory">{closing.headline_line1}{closing.headline_line2 && <span className="accent-block">{closing.headline_line2}</span>}</h2>
            </Reveal>
            <Reveal delay={150} className="space-y-6">
              {closing.body && <p className="text-ivory/80 font-light leading-relaxed text-base md:text-lg">{closing.body}</p>}
              {closing.show_cta !== false && (
                <div className="flex flex-wrap gap-4">
                  <CTA config={closing.primary_cta} onEnquire={openEnquiry} variant="onDarkPrimary" testId="cta-enquire" />
                  <CTA config={closing.secondary_cta} onEnquire={openEnquiry} variant="onDarkGhost" testId="cta-partner" />
                </div>
              )}
            </Reveal>
          </div>
        </section>
      )}
    </>
  );
}
